import env from "./env";

export type AppId =
  | "admin_hub"
  | "market_analyzer"
  | "data_studio"
  | "sales_copilot"
  | "ai_sales_copilot";

/**
 * Returns the external URL for a given app/module, or null if the URL env var
 * isn't configured. Used by the v2 RBAC app switcher in the topbar.
 *
 * The current app (market_analyzer here) is intentionally included so callers
 * can render it as the current/active item; selecting it is a no-op.
 */
/**
 * URL of the admin AI-scoring authoring tool for the current tenant, or null if
 * the admin base URL or tenant id is missing. Scoring model authoring lives in
 * the admin app (argo), so MA deep-links out to it instead of authoring inline.
 */
export function getAiScoringUrl(tenantId: string | null): string | null {
  const base = env.ADMIN_URL;
  if (!base || !tenantId) return null;
  return `${base.replace(/\/$/, "")}/org/${tenantId}/ai-scoring`;
}

/**
 * Opens the admin AI-scoring tool for the tenant in a new tab. Returns true if
 * it navigated, false if the URL couldn't be built (so callers can fall back to
 * the legacy in-app flow).
 */
export function openAiScoring(tenantId: string | null): boolean {
  const url = getAiScoringUrl(tenantId);
  if (!url) return false;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

/**
 * URL of the admin editor for a specific AI scoring model (its detail page), or
 * null if the admin base URL / ids are missing. Editing an AI model lives in the
 * admin app, so MA deep-links out to the model by its `_id`.
 */
export function getAiScoringModelUrl(
  tenantId: string | null,
  modelId: string | null
): string | null {
  const base = env.ADMIN_URL;
  if (!base || !tenantId || !modelId) return null;
  return `${base.replace(/\/$/, "")}/org/${tenantId}/scoring/${modelId}`;
}

/** Opens the admin editor for a specific AI scoring model in a new tab. */
export function openAiScoringModel(
  tenantId: string | null,
  modelId: string | null
): boolean {
  const url = getAiScoringModelUrl(tenantId, modelId);
  if (!url) return false;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
}

export function getAppUrl(
  appId: AppId,
  options: { tenantId: string | null }
): string | null {
  const { tenantId } = options;

  switch (appId) {
    case "admin_hub": {
      const base = env.ADMIN_URL;
      if (!base) return null;
      const trimmed = base.replace(/\/$/, "");
      // Same destination as the sidebar's "Admin Center": the org home is
      // hidden for Market-Analyzer-only tenants, so land on settings (RGI-2609).
      return tenantId ? `${trimmed}/org/${tenantId}/settings/account` : trimmed;
    }
    case "market_analyzer":
      // Same-origin; the app switcher no-ops on the current app, so this is
      // only useful as a stable href value.
      return "/projects";
    case "data_studio": {
      const base = env.STUDIO_URL;
      if (!base) return null;
      const trimmed = base.replace(/\/$/, "");
      return tenantId ? `${trimmed}/tenant/${tenantId}` : trimmed;
    }
    case "sales_copilot": {
      const base = env.COPILOT_URL;
      if (!base) return null;
      return base.replace(/\/$/, "");
    }
    case "ai_sales_copilot": {
      const base = env.HSI_URL;
      if (!base) return null;
      return base.replace(/\/$/, "");
    }
    default:
      return null;
  }
}
